import { ExternalLink } from "lucide-react";
import { socialLinks, socialProfiles, type SocialKey } from "../config/socialLinks";

export function SocialIcon({ name, className = "h-5 w-5" }: { name: SocialKey; className?: string }) {
  const common = { className, "aria-hidden": true, focusable: false } as const;
  if (name === "discord") {
    return (
      <svg viewBox="0 0 24 24" fill="currentColor" {...common}>
        <path d="M20.3 4.4A16.9 16.9 0 0 0 16.1 3l-.2.4c-.2.4-.4.9-.6 1.3a15.7 15.7 0 0 0-4.6 0c-.2-.5-.4-.9-.7-1.3L9.9 3a17.3 17.3 0 0 0-4.2 1.4C3 8.4 2.3 12.3 2.7 16.1A17 17 0 0 0 7.8 19l1-1.6c-.6-.2-1.1-.5-1.6-.8l.4-.3a12.2 12.2 0 0 0 8.8 0l.4.3c-.5.3-1 .6-1.6.8l1 1.6a17 17 0 0 0 5.1-2.9c.5-4.4-.8-8.2-3-11.7ZM8.6 13.8c-.9 0-1.6-.8-1.6-1.8s.7-1.8 1.6-1.8 1.6.8 1.6 1.8-.7 1.8-1.6 1.8Zm6.8 0c-.9 0-1.6-.8-1.6-1.8s.7-1.8 1.6-1.8S17 11 17 12s-.7 1.8-1.6 1.8Z" />
      </svg>
    );
  }
  if (name === "twitter") {
    return (
      <svg viewBox="0 0 24 24" fill="currentColor" {...common}>
        <path d="M17.7 3h3.1l-6.7 7.7L22 21h-6.2l-4.9-6.4L5.3 21H2.2l7.2-8.3L2 3h6.4l4.4 5.8L17.7 3Zm-1.1 16.2h1.7L7.5 4.7H5.7l10.9 14.5Z" />
      </svg>
    );
  }
  if (name === "youtube") {
    return (
      <svg viewBox="0 0 24 24" fill="currentColor" {...common}>
        <path d="M23 7.2a3 3 0 0 0-2.1-2.1C19 4.6 12 4.6 12 4.6s-7 0-8.9.5A3 3 0 0 0 1 7.2 31 31 0 0 0 .5 12a31 31 0 0 0 .5 4.8 3 3 0 0 0 2.1 2.1c1.9.5 8.9.5 8.9.5s7 0 8.9-.5a3 3 0 0 0 2.1-2.1 31 31 0 0 0 .5-4.8 31 31 0 0 0-.5-4.8ZM9.7 15.4V8.6l6 3.4-6 3.4Z" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...common}>
      <path d="M16.7 3c.4 3 2 4.8 4.8 5v3.4a8.3 8.3 0 0 1-4.8-1.5v6.1c0 3.1-2.3 5.5-5.6 5.5A5.5 5.5 0 0 1 5.5 16c0-3.4 2.8-5.9 6.2-5.5V14c-1.4-.4-2.7.5-2.7 2a2 2 0 0 0 2.1 2c1.3 0 2.1-.8 2.1-2.2V3h3.5Z" />
    </svg>
  );
}

export function DiscordButton({ className = "btn-secondary", label = "Unirme al Discord", ariaLabel }: { className?: string; label?: string; ariaLabel?: string }) {
  return (
    <a className={className} href={socialLinks.discord} target="_blank" rel="noopener noreferrer" {...(ariaLabel ? { "aria-label": ariaLabel } : {})}>
      <SocialIcon name="discord" />
      {label}
      <ExternalLink size={16} aria-hidden="true" />
    </a>
  );
}

export function SocialCards() {
  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {socialProfiles.map((profile) => (
        <a
          key={profile.key}
          href={socialLinks[profile.key]}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={profile.ariaLabel}
          className="group flex min-h-64 flex-col rounded-lg border border-line bg-panel/80 p-5 shadow-glow transition hover:-translate-y-1 hover:border-electric/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-electric motion-reduce:transition-none motion-reduce:hover:translate-y-0"
        >
          <span className={`grid h-14 w-14 place-items-center rounded-lg border border-white/10 bg-gradient-to-br ${profile.tone}`}>
            <SocialIcon name={profile.key} className="h-7 w-7" />
          </span>
          <h3 className="mt-5 text-xl font-black text-white">{profile.name}</h3>
          <p className="mt-2 flex-1 text-sm leading-6 text-slate-400">{profile.description}</p>
          <span className="mt-5 inline-flex min-h-11 items-center justify-center gap-2 rounded-md border border-electric/30 bg-electric/10 px-4 py-2 font-bold text-electric group-hover:bg-electric group-hover:text-ink">
            {profile.buttonLabel}
            <ExternalLink size={16} aria-hidden="true" />
          </span>
        </a>
      ))}
    </div>
  );
}

export function SocialIconLinks() {
  return (
    <div className="flex flex-wrap gap-2">
      {socialProfiles.map((profile) => (
        <a
          key={profile.key}
          href={socialLinks[profile.key]}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={profile.ariaLabel}
          className="grid h-11 w-11 place-items-center rounded-md border border-line bg-white/5 text-slate-200 transition hover:border-electric/50 hover:text-electric focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-electric"
        >
          <SocialIcon name={profile.key} />
        </a>
      ))}
    </div>
  );
}
